import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Loader2, CheckCircle2, Clock, AlertCircle, Upload, Mail, FileText, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status configurations — three-phase model
const statusConfig = {
  under_review: { color: "bg-slate-100 text-slate-700", label: "Under Review", icon: Clock },
  preparing: { color: "bg-blue-100 text-blue-700", label: "Preparing Next Step", icon: Clock },
  closed: { color: "bg-gray-100 text-gray-500", label: "Closed", icon: X },
  action_required: { color: "bg-amber-100 text-amber-700", label: "Action Required", icon: AlertCircle },
  client_action_required: { color: "bg-orange-100 text-orange-700", label: "Awaiting Documents", icon: FileText },
  submitted: { color: "bg-green-100 text-green-700", label: "Submitted", icon: CheckCircle2 },
  documents_needed: { color: "bg-orange-100 text-orange-700", label: "Awaiting Documents", icon: FileText },
  intake_complete: { color: "bg-emerald-100 text-emerald-700", label: "Intake Complete", icon: CheckCircle2 },
  in_review: { color: "bg-purple-100 text-purple-700", label: "Under Review", icon: Clock },
  contract_signed: { color: "bg-teal-100 text-teal-700", label: "Contract Signed", icon: FileText },
  scheduled: { color: "bg-cyan-100 text-cyan-700", label: "Scheduled", icon: CheckCircle2 },
  in_progress: { color: "bg-indigo-100 text-indigo-700", label: "In Progress", icon: Clock },
  completed: { color: "bg-green-100 text-green-700", label: "Complete", icon: CheckCircle2 },
  intake_requested: { color: "bg-amber-100 text-amber-700", label: "Action Required", icon: AlertCircle },
  intake_received: { color: "bg-green-100 text-green-700", label: "Submitted", icon: CheckCircle2 },
  awaiting_documents: { color: "bg-orange-100 text-orange-700", label: "Awaiting Documents", icon: FileText },
  intake_completed: { color: "bg-emerald-100 text-emerald-700", label: "Intake Complete", icon: CheckCircle2 },
  in_service: { color: "bg-indigo-100 text-indigo-700", label: "In Progress", icon: Clock },
  service_completed: { color: "bg-green-100 text-green-700", label: "Complete", icon: CheckCircle2 }
};

export default function ClientPortalPage() {
  const { submissionId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portalData, setPortalData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadingDocId, setUploadingDocId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRefs = useRef({});
  
  useEffect(() => { fetchPortalData(); }, [submissionId, token]);
  
  const fetchPortalData = async () => {
    try {
      setLoading(true);
      setError(null);
      const url = token ? `${API}/client/${submissionId}?token=${token}` : `${API}/client/${submissionId}`;
      const response = await axios.get(url);
      if (response.data.required_documents) {
        response.data.required_documents = response.data.required_documents.map(doc => ({
          ...doc, doc_id: doc.doc_id || doc.id
        }));
      }
      setPortalData(response.data);
      if (response.data.documents_submitted_at) setSubmitted(true);
    } catch (err) {
      if (err.response?.status === 404) setError("This submission was not found. Please check the link and try again.");
      else if (err.response?.status === 403) setError("Invalid access link. Please use the link from your email.");
      else setError("Failed to load portal. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleExternalUploadClick = async () => {
    try { await axios.post(`${API}/submissions/${submissionId}/track-upload-click`); } catch {}
    window.open(portalData.external_upload_link, "_blank");
  };
  
  const handleSubmitDocuments = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions/${submissionId}/submit-documents`);
      setSubmitted(true);
      await fetchPortalData();
    } catch { toast.error("Failed to submit documents. Please try again."); }
    finally { setSubmitting(false); }
  };
  
  const handleFileUpload = async (docId, file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { toast.error("File too large. Maximum size is 50MB."); return; }
    setUploadingDocId(docId);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (token) formData.append("token", token);
      const response = await axios.post(`${API}/portal/submissions/${submissionId}/documents/${docId}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / e.total))
      });
      if (response.data.status === "success") { toast.success(`${file.name} uploaded`); await fetchPortalData(); }
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed. Please try again."); }
    finally { setUploadingDocId(null); setUploadProgress(0); }
  };
  
  const handleFileInputChange = (docId, event) => {
    const file = event.target.files?.[0];
    if (file) handleFileUpload(docId, file);
    event.target.value = "";
  };
  
  const handleDrop = (docId, event) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files?.[0];
    if (file) handleFileUpload(docId, file);
  };
  
  const handleDragOver = (event) => { event.preventDefault(); event.stopPropagation(); };

  // ─── Loading / Error states ───
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 max-w-md w-full p-8 text-center">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <h2 className="text-base font-semibold mb-1">Unable to Load Portal</h2>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  // ─── Destructure portal data ───
  const {
    client_name, client_email, status, status_label, status_message,
    business_name, business_email, business_logo_url, primary_color,
    branding: portalBranding, is_professional,
    required_documents, document_handling_method, document_email,
    external_upload_link, native_upload_enabled, documents_submitted_at,
    show_intake_form, intake_form_link, lead_status, intake_stage,
    scheduling_url, scheduling_button_text, show_scheduling_link
  } = portalData;

  // Resolve branding
  const brand = portalBranding || {};
  const accentColor = brand.primary_color || primary_color || "#1a4d2e";
  const btnColor = brand.button_color || accentColor;
  const bgColor = brand.background_color || "#ffffff";

  const logoUrl = (() => {
    const raw = brand.logo_url || business_logo_url;
    if (!raw) return null;
    if (raw.startsWith("http")) return raw;
    if (raw.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${raw}`;
    return raw;
  })();

  const intake_form_submitted = portalData.intake_form_submitted;
  
  let displayStatus = status;
  if (intake_form_submitted && (status === "action_required" || intake_stage === "client_action_required")) {
    displayStatus = "client_action_required";
  }
  const statusInfo = statusConfig[displayStatus] || statusConfig[intake_stage] || statusConfig.under_review || { color: "bg-slate-100 text-slate-700", icon: Clock, label: status_label || "Under Review" };
  const StatusIcon = statusInfo.icon;
  const buttonStyle = { backgroundColor: btnColor, borderColor: btnColor, color: "#ffffff" };

  // Document stats
  const uploadedDocs = required_documents?.filter(d => d.status === "uploaded" || d.status === "reviewed") || [];
  const pendingDocs = required_documents?.filter(d => d.status === "pending") || [];
  const requiredPendingDocs = pendingDocs.filter(d => d.required);
  const allRequiredUploaded = requiredPendingDocs.length === 0;
  const hasDocuments = required_documents && required_documents.length > 0;
  const showDocumentSection = hasDocuments && document_handling_method !== "none";

  // ─── Document upload renderer ───
  const renderDocumentUpload = () => {
    if (submitted || documents_submitted_at) {
      return (
        <div className="py-4 text-center" data-testid="upload-success-state">
          <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
          <p className="text-sm font-medium text-green-800">Documents Received</p>
          <p className="text-xs text-slate-500 mt-1">We're reviewing your documents.</p>
        </div>
      );
    }
    if (external_upload_link) {
      return (
        <div className="space-y-3 text-center" data-testid="upload-state-external">
          <p className="text-sm text-slate-600">Upload your documents using the link below.</p>
          <Button className="w-full h-10" style={buttonStyle} onClick={handleExternalUploadClick} data-testid="portal-external-upload-btn">
            <ExternalLink className="w-4 h-4 mr-2" /> Open Upload Link
          </Button>
        </div>
      );
    }
    if (native_upload_enabled) {
      return (
        <div className="space-y-3" data-testid="upload-state-native">
          {uploadedDocs.map((doc, idx) => (
            <div key={doc.doc_id || `u-${idx}`} className="flex items-center gap-3 p-3 bg-green-50 border border-green-100 rounded-xl">
              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span className="text-sm font-medium flex-1">{doc.label}</span>
              <Badge className="bg-green-100 text-green-700 text-[10px]">Uploaded</Badge>
            </div>
          ))}
          {pendingDocs.map((doc, idx) => (
            <div key={doc.doc_id || `p-${idx}`}
              className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:border-slate-300 transition-colors cursor-pointer"
              onDrop={(e) => handleDrop(doc.doc_id, e)} onDragOver={handleDragOver}
              onClick={() => fileInputRefs.current[doc.doc_id]?.click()}
              data-testid={`upload-zone-${doc.doc_id}`}>
              <input ref={(el) => fileInputRefs.current[doc.doc_id] = el} type="file" className="hidden" accept=".pdf,.docx,.doc,.jpg,.jpeg,.png" onChange={(e) => handleFileInputChange(doc.doc_id, e)} />
              {uploadingDocId === doc.doc_id ? (
                <div className="space-y-2">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                  <p className="text-xs text-slate-500">Uploading... {uploadProgress}%</p>
                  <Progress value={uploadProgress} className="h-1.5" />
                </div>
              ) : (
                <>
                  <Upload className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                  <p className="text-sm font-medium">{doc.label}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Drop file or click to upload{doc.required ? "" : " (optional)"}</p>
                </>
              )}
            </div>
          ))}
          {pendingDocs.length > 0 && allRequiredUploaded && (
            <Button className="w-full h-10 mt-1" style={buttonStyle} onClick={handleSubmitDocuments} disabled={submitting} data-testid="submit-documents-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Submit Documents
            </Button>
          )}
        </div>
      );
    }
    if (document_handling_method === "email") {
      return (
        <div className="space-y-3 text-center" data-testid="upload-state-email">
          <Mail className="w-6 h-6 text-slate-400 mx-auto" />
          {document_email ? (
            <>
              <p className="text-sm text-slate-600">Email your documents to:</p>
              <button onClick={() => { navigator.clipboard.writeText(document_email); toast.success("Email copied!"); }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 border rounded-lg text-sm font-mono hover:bg-slate-100 transition-colors">
                {document_email}
              </button>
            </>
          ) : (
            <p className="text-sm text-slate-600">Reply to the email you received with your documents attached.</p>
          )}
        </div>
      );
    }
    return (
      <div className="py-4 text-center" data-testid="upload-state-pending">
        <Clock className="w-6 h-6 text-slate-400 mx-auto mb-2" />
        <p className="text-sm text-slate-600">Your documents are being processed.</p>
      </div>
    );
  };

  // =========================================================================
  // RENDER — Premium SaaS Portal
  // =========================================================================
  return (
    <div className="min-h-screen py-8 px-4 bg-[#FAFAFA]">
      <div className="max-w-[560px] mx-auto space-y-4">
        
        {/* ─── Header ─── */}
        <div className="text-center pt-2 pb-1">
          {logoUrl ? (
            <img src={logoUrl} alt={business_name} className="h-12 mx-auto mb-2 object-contain"
              onError={(e) => { e.target.style.display = "none"; }} data-testid="portal-business-logo" />
          ) : (
            <div className="w-9 h-9 rounded-lg flex items-center justify-center mx-auto mb-2" style={{ backgroundColor: `${accentColor}12` }}>
              <span className="text-base font-bold" style={{ color: accentColor }}>{(business_name || "B")[0]}</span>
            </div>
          )}
          <h1 className="text-lg font-bold text-slate-900">{business_name}</h1>
        </div>

        {/* ─── Client + Status ─── */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="font-semibold text-slate-900 truncate">{client_name || "Client"}</p>
              {client_email && <p className="text-xs text-slate-400 mt-0.5 truncate">{client_email}</p>}
            </div>
            <Badge className={`${statusInfo.color} text-xs flex-shrink-0`} data-testid="portal-status-badge">
              <StatusIcon className="w-3 h-3 mr-1" /> {statusInfo.label}
            </Badge>
          </div>
          {status_message && (
            <p className="text-sm text-slate-600 mt-3 pt-3 border-t border-slate-50">{status_message}</p>
          )}
        </div>

        {/* ─── Scheduling CTA ─── */}
        {show_scheduling_link && scheduling_url && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 px-5 py-4 text-center">
            <Button className="rounded-full px-6 h-9 text-sm" style={buttonStyle}
              onClick={() => window.open(scheduling_url, "_blank")} data-testid="portal-schedule-btn">
              <Clock className="w-3.5 h-3.5 mr-1.5" /> {scheduling_button_text || "Schedule a Call"}
            </Button>
          </div>
        )}

        {/* ─── Intake Form CTA ─── */}
        {show_intake_form && intake_form_link && (
          <div className="bg-white rounded-2xl shadow-sm border-2 border-amber-200 px-5 py-5 text-center">
            <FileText className="w-6 h-6 text-amber-500 mx-auto mb-2" />
            <h3 className="font-semibold text-sm mb-1">Complete Your Intake</h3>
            <p className="text-xs text-slate-500 mb-3">Fill out the intake form to proceed.</p>
            <Button className="rounded-full px-5 h-9 text-sm" style={buttonStyle}
              onClick={() => window.location.href = intake_form_link} data-testid="start-intake-btn">
              Start Intake
            </Button>
          </div>
        )}

        {/* ─── Documents ─── */}
        {showDocumentSection && (
          <div id="document-section" className="bg-white rounded-2xl shadow-sm border border-slate-100 px-5 py-4 space-y-3">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" style={{ color: accentColor }} />
              <h3 className="font-semibold text-sm">Documents</h3>
              {hasDocuments && (
                <span className="text-xs text-slate-400 ml-auto">{uploadedDocs.length}/{required_documents.length}</span>
              )}
            </div>
            {!submitted && !documents_submitted_at && document_handling_method !== "email" && hasDocuments && (
              <>
                <div className="space-y-1.5">
                  {required_documents.map((doc, idx) => {
                    const done = doc.status === "uploaded" || doc.status === "reviewed";
                    return (
                      <div key={doc.doc_id || idx} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${done ? "bg-green-50" : "bg-slate-50"}`} data-testid={`doc-item-${doc.doc_id}`}>
                        {done ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" /> : <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" />}
                        <span className="flex-1">{doc.label}</span>
                        {!done && <span className={`text-[10px] ${doc.required ? "text-red-500" : "text-slate-400"}`}>{doc.required ? "Required" : "Optional"}</span>}
                      </div>
                    );
                  })}
                </div>
                <Separator />
              </>
            )}
            {renderDocumentUpload()}
          </div>
        )}

        {/* ─── Success banner ─── */}
        {(submitted || documents_submitted_at) && !showDocumentSection && (
          <div className="bg-white rounded-2xl shadow-sm border border-green-100 px-5 py-4 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-800">Documents Received</p>
              <p className="text-xs text-green-600">We're reviewing your submission.</p>
            </div>
          </div>
        )}

        {/* ─── Contact ─── */}
        {business_email && business_email.includes("@") && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 px-5 py-3 text-center">
            <p className="text-xs text-slate-400">
              Questions? <a href={`mailto:${business_email}`} className="font-medium text-blue-600 hover:underline">{business_email}</a>
            </p>
          </div>
        )}

        {/* ─── Footer — hidden for professional ─── */}
        {!is_professional && (
          <p className="text-center text-[11px] text-slate-300 py-2">
            Powered by <span className="font-medium">HeyIntake</span>
          </p>
        )}
      </div>
    </div>
  );
}
